import {
	Button,
	Field,
	Input,
	makeStyles,
	Radio,
	RadioGroup,
	Label,
	Checkbox,
	Textarea,
	RadioGroupOnChangeData,
	CheckboxOnChangeData,
	tokens,
	TextareaOnChangeData,
	InputOnChangeData,
	createTableColumn,
	TableCellLayout,
	Avatar
} from '@fluentui/react-components';
import { DatePicker } from "@fluentui/react-datepicker-compat";
import React, { ChangeEvent, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Addons } from '../components/Addons';
import { Server } from '../apis';
import { CustomerSearch } from '../components/CustomerSearch';
import { type ICustomer, CadSystems, Catia, Creo, Inventor, LicenseType, NX, PublishEdition, SDK, Solidworks, WorkstationEnterpriseEdition, WorkstationProfessionalEdition } from '../IDB';
import { useNavigate } from 'react-router';
import { Organization20Regular } from '@fluentui/react-icons';

const useStyles = makeStyles({
	date: {
		maxWidth: "300px",
	},
	container: {
		marginLeft: '260px', // Width of navbar
		height: '100vh',
		'& 	.fui-Field label': {
			fontWeight: 'bold'
		}
	},
	label: {
		fontWeight: 'bold'
	},
	center: {
		display: 'flex',
		justifyContent: 'center',
		height: '100%',
		overflowY: 'scroll'
	},
	alignCenter: {
		alignItems: 'center',
	},
	alignStart: {
		alignItems: 'start'
	},
	formList: {
		width: '500px',
	},
	show: {
		opacity: 1,
		height: 'auto',
		transition: 'opacity 0.5s ease, margin-bottom 0.5s ease',
		marginBottom: tokens.spacingVerticalXXXL,
	},
	licType: {
		width: '100%',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'baseline',
		margin: `${tokens.spacingVerticalXXXL} 0px`,
	},
	baseField: {
		opacity: 0,
		height: 0,
		width: '100%',
	},
	notes: {
		marginTop: tokens.spacingVerticalXXXL
	},
	footer: {
		margin: '20px auto',
		width: '100px',
		opacity: 0
	},
	group: {
		display: 'inline-block',
		verticalAlign: 'middle',
		paddingRight: tokens.spacingHorizontalM,
		'& label': {
			fontWeight: 'normal !important',
		},
	},
	floatingLabel: {
		width: '175px',
		padding: `${tokens.spacingVerticalMNudge} 0px`
	},
	radioHover: {
		"& input:not(:checked):hover ~ label": {
			color: 'black !important'
		}
	}
});

export const licenseMap = {
	"NEW": "New Commercial License",
	"EVAL": "Evaluation",
	"ANARK": "Anark Internal Use"
}

interface ILicenseBuilder {
	author: string;
}

let stillTypingTimeoutLicenseBuilder: NodeJS.Timeout;
export const LicenseBuilder = (props: ILicenseBuilder) => {

	const server = useMemo(() => Server.getInstance(), []);
	const styles = useStyles();
	const navigate = useNavigate();

	const [expiry, setExpiry] = useState<string>("");
	const [productflag, setProductFlag] = useState<typeof PublishEdition | typeof WorkstationEnterpriseEdition | typeof WorkstationProfessionalEdition>(PublishEdition);
	const [floating, setFloating] = useState<boolean>(productflag === WorkstationEnterpriseEdition || productflag === WorkstationProfessionalEdition);
	const [nxAddon, setNxAddon] = useState<boolean>(false);
	const [catiaAddon, setCatiaAddon] = useState<boolean>(false);
	const [inventorAddon, setInventorAddon] = useState<boolean>(false);
	const [creoAddon, setCreoAddon] = useState<boolean>(false);
	const [solidworksAddon, setSolidworksAddon] = useState<boolean>(false);
	const [sdkAddon, setSdkAddon] = useState<boolean>(false);
	const [invoice, setInvoice] = useState<string>("");
	const [count, setCount] = useState<number>(0);
	const [licType, setLicType] = useState<LicenseType>();
	const [customer, setCustomer] = useState<ICustomer | null>(null);
	const [notes, setNotes] = useState<string>("")
	const [generalError, setGeneralError] = useState<string>("");
	const [countError, setCountError] = useState<string>("");

	const handleAddedNotes = (ev: ChangeEvent<HTMLTextAreaElement>, data: TextareaOnChangeData) => {
		setNotes(data.value);
	};

	const handleAddonSelection = (selectedOptions: CadSystems[]) => {
		setNxAddon(false);
		setCatiaAddon(false);
		setInventorAddon(false);
		setCreoAddon(false);
		setSolidworksAddon(false);
		setSdkAddon(false);
		selectedOptions.forEach((item) => {
			switch (item) {
				case Creo:
					setCreoAddon(true);
					break;
				case NX:
					setNxAddon(true);
					break;
				case Catia:
					setCatiaAddon(true);
					break;
				case Inventor:
					setInventorAddon(true);
					break;
				case Solidworks:
					setSolidworksAddon(true);
					break;
				case SDK:
					setSdkAddon(true);
					break;
			}
		});
	};

	const onTypeChange = (ev: React.FormEvent<HTMLDivElement>, data: RadioGroupOnChangeData) => {
		setLicType(data.value as LicenseType);
	};

	const onChecked = (ev: React.ChangeEvent<HTMLInputElement>, data: CheckboxOnChangeData) => {
		setFloating(data.checked === 'mixed' ? false : data.checked);
	};

	const onSwitch = (ev: React.FormEvent<HTMLDivElement>, data: RadioGroupOnChangeData) => {
		if (data.value === WorkstationEnterpriseEdition) {
			setProductFlag(WorkstationEnterpriseEdition);
			setFloating(true);
		} else if (data.value === WorkstationProfessionalEdition) {
			setProductFlag(WorkstationProfessionalEdition);
			setFloating(true);
			setNxAddon(false);
			setCatiaAddon(false);
			setInventorAddon(false);
			setCreoAddon(false);
			setSolidworksAddon(false);
			setSdkAddon(false);
		} else {
			setProductFlag(PublishEdition);
			setFloating(false);
		}
	};

	const onSelected = (customer: ICustomer) => {
		setCustomer(customer);
	};

	const submitLicense = async (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		const addons: CadSystems[] = [];
		if (nxAddon) addons.push(NX);
		if (catiaAddon) addons.push(Catia);
		if (inventorAddon) addons.push(Inventor);
		if (creoAddon) addons.push(Creo);
		if (sdkAddon) addons.push(SDK);
		if (solidworksAddon) addons.push(Solidworks);
		
		if (!floating && count > 1) {
			setCountError("Activation count cannot exceed 1 for node-locked licenses!");
			return;
		}

		if (customer && licType && expiry && count && (licType === "NEW" ? invoice : true)) {
			try {
				const completeLicense = await server.createLicense({
					customer: `${customer.id}`,
					edition: productflag,
					expiry: expiry,
					licenseType: licType,
					author: props.author,
					addons,
					invoice,
					allowed: count,
					floating: floating,
					notes: notes
				});
				navigate(`/customers/${customer.id}#${completeLicense.code}`);
			} catch (error) {
				console.log(error)
			}
		} else {
			setGeneralError('Customer, expiration date, activation count, and invoice number are required!');
		}
	};

	const searchCustomerColumns = [
		createTableColumn<ICustomer>({
			columnId: "name",
			renderHeaderCell: () => {
				return "Name";
			},
			renderCell: (item) => {
				return (
					<TableCellLayout media={
						<Avatar
							aria-label={item.name}
							name={item.name}
						/>
					}>
						{item.name}
					</TableCellLayout>
				);
			},
		}),
		createTableColumn<ICustomer>({
			columnId: "company",
			renderHeaderCell: () => {
				return "Company";
			},
			renderCell: (item) => {
				return (
					<TableCellLayout media={<Organization20Regular />}>
						{item.company.name}
					</TableCellLayout>
				);
			},
		})
	];

	let maxDate = new Date();
	if (licType === "EVAL") {
		maxDate.setDate(maxDate.getDate() + 60);
	} else {
		maxDate.setDate(maxDate.getDate() + (365 * 3));
	}

	const classNames = {
		"licType": styles.licType,
		"customer": styles.baseField,
		"invoice": styles.baseField,
		"expiry": styles.baseField,
		"count": styles.baseField,
		"app": styles.baseField,
		"footer": styles.footer,
	};

	if (licType) {
		classNames.licType += ' ' + styles.show;
		classNames.customer += ' ' + styles.show;
	}

	if (customer && licType === 'NEW') {
		classNames.invoice += ' ' + styles.show;
	} 
	
	if ((customer && licType) || invoice) {
		classNames.expiry += ' ' + styles.show;
	}

	if (expiry) {
		classNames.count += ' ' + styles.show;
	}

	if (count) {
		classNames.app += ' ' + styles.show;
		classNames.footer += ' ' + styles.show;
	}

	const typingCallback = (data: InputOnChangeData, cb: Function) => {
		clearTimeout(stillTypingTimeoutLicenseBuilder);
		stillTypingTimeoutLicenseBuilder = setTimeout((): void => {
			cb(data.value);
		}, 500);
	};

	const containerRef = useRef<HTMLDivElement>(null);
	const [containerAlignment, setContainerAlignment] = useState<string>(`${styles.center} ${styles.alignCenter}`);
	
	useLayoutEffect(() => {
		if (containerRef.current) {
			const currentHeight = containerRef.current.clientHeight || 0;
			const pageHeight = window.innerHeight;
			if (currentHeight > pageHeight) {
				setContainerAlignment(`${styles.center} ${styles.alignStart}`);
				containerRef.current.scrollTop = containerRef.current.scrollHeight;
			}
		}
	}, [licType, customer, expiry, count, productflag]);

	return (
		<div className={styles.container}>
			<div ref={containerRef} className={containerAlignment}>
				<div id="formList" className={styles.formList}>
					<div key="licType" className={classNames.licType}>
						<Label className={styles.label} required>License Type</Label>
						<RadioGroup layout="horizontal-stacked" className={styles.group} onChange={onTypeChange}>
							<Radio value="NEW" label={licenseMap.NEW} className={styles.radioHover} />
							<Radio value="EVAL" label={licenseMap.EVAL} className={styles.radioHover} />
							<Radio value="ANARK" label={licenseMap.ANARK} className={styles.radioHover} />
						</RadioGroup>
					</div>
					<div key="customer" className={classNames.customer}>
						<CustomerSearch
							columns={searchCustomerColumns}
							onSelected={onSelected}
							sortable={false}
						/>
					</div>
					<div key="invoice" className={classNames.invoice}>
						<Field label="Invoice Number" required>
							<Input onChange={(e, data: InputOnChangeData) => {
								typingCallback(data, setInvoice);
							}} />
						</Field>
						<Field label="Additional Notes" className={styles.notes}>
							<Textarea onChange={handleAddedNotes} />
						</Field>
					</div>
					<div key="expiry" className={classNames.expiry}>
						<Field label="Expiration Date" required>
							<DatePicker
								className={styles.date}
								placeholder="Select a date..."
								allowTextInput
								onSelectDate={(date: Date | null) => setExpiry(date?.toISOString() || "")}
								minDate={new Date()}
								maxDate={maxDate}
							/>
						</Field>
					</div>
					<div key="count" className={classNames.count}>
						<Field label="Activation Count" required validationMessage={countError ? countError : null} validationState={countError ? 'error' : 'none'}>
							<Input type='number' onChange={(e, data: InputOnChangeData) => {
								typingCallback(data, setCount);
							}} />
						</Field>
					</div>
					<div key="app" className={classNames.app}>
						<Field label="Application Type" required>
							<RadioGroup layout="horizontal-stacked" defaultValue={licType} className={styles.group} onChange={onSwitch}>
								<Radio value="PUB" label="Publish" defaultChecked className={styles.radioHover} />
								<Radio value="WS_EN" label="Workstation Enterprise" className={styles.radioHover} />
								<Radio value="WS_PR" label="Workstation Professional" className={styles.radioHover} />
							</RadioGroup>
							<Field label="Floating" orientation='horizontal' className={styles.floatingLabel}>
								<Checkbox
									checked={floating}
									onChange={onChecked}
								/>
							</Field>
							{productflag === WorkstationEnterpriseEdition || productflag === PublishEdition ? <Addons
								onSelection={handleAddonSelection}
							/> : null}
						</Field>
					</div>
					<div className={classNames.footer}>
						<Field validationMessage={generalError ? generalError : ''} validationState={generalError ? 'error' : 'none'}>
							<Button appearance='primary' onClick={submitLicense} style={{ width: '100%' }}>Create</Button>
						</Field>
					</div>
				</div>
			</div>
		</div>
	)
};